import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Settings, Check, Shield } from 'lucide-react';

import { Button } from '../ui';
import { useCookieConsent, CookieConsent } from '../../context/CookieConsentContext';

/**
 * @component CookieConsentBanner
 * @description Banner de consentimiento de cookies con opciones de configuración
 * Especificaciones: GDPR-like compliance, accesibilidad, configuración granular
 */

const CookieConsentBanner: React.FC = () => {
  const { showBanner, acceptAll, rejectAll, updateConsent, hideBanner } = useCookieConsent();
  const [showSettings, setShowSettings] = useState(false);
  const [tempConsent, setTempConsent] = useState<CookieConsent>({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  if (!showBanner) {
    return null;
  }

  const handleSaveSettings = () => {
    updateConsent(tempConsent);
    setShowSettings(false);
    hideBanner();
  };

  const handleToggleCategory = (category: keyof CookieConsent) => {
    if (category === 'essential') return; // No se puede desactivar
    
    setTempConsent(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const cookieCategories = [
    {
      key: 'essential' as keyof CookieConsent,
      name: 'Cookies esenciales',
      description: 'Necesarias para el funcionamiento básico del sitio web',
      required: true,
    },
    {
      key: 'analytics' as keyof CookieConsent,
      name: 'Cookies de análisis',
      description: 'Nos ayudan a entender cómo los usuarios interactúan con el sitio',
      required: false,
    },
    {
      key: 'marketing' as keyof CookieConsent,
      name: 'Cookies de marketing',
      description: 'Utilizadas para mostrar anuncios relevantes y medir su efectividad',
      required: false,
    },
    {
      key: 'preferences' as keyof CookieConsent,
      name: 'Cookies de preferencias',
      description: 'Recuerdan sus preferencias y configuraciones personalizadas',
      required: false,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
      <div className="w-full max-w-4xl pointer-events-auto">
        <div className="bg-white border border-gray-200 rounded-lg shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Configuración de cookies
              </h2>
            </div>
            <button
              onClick={hideBanner}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              aria-label="Cerrar banner de cookies"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {!showSettings ? (
              // Vista simple
              <div>
                <p className="text-gray-700 mb-4">
                  Utilizamos cookies para mejorar su experiencia en nuestro sitio web, 
                  analizar el tráfico y personalizar el contenido. Puede elegir qué 
                  tipos de cookies acepta.
                </p>
                <p className="text-sm text-gray-600 mb-6">
                  Para más información, consulte nuestra{' '}
                  <Link 
                    to="/cookies" 
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    política de cookies
                  </Link>{' '}
                  y{' '}
                  <Link 
                    to="/privacidad" 
                    className="text-primary-600 hover:text-primary-700 underline"
                  >
                    política de privacidad
                  </Link>.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={acceptAll} className="flex-1">
                    <Check className="h-4 w-4 mr-2" />
                    Aceptar todas
                  </Button>
                  <Button 
                    onClick={rejectAll} 
                    variant="outline" 
                    className="flex-1"
                  >
                    Solo esenciales
                  </Button>
                  <Button 
                    onClick={() => setShowSettings(true)} 
                    variant="outline"
                    className="flex-1"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Configurar
                  </Button>
                </div>
              </div>
            ) : (
              // Vista de configuración detallada
              <div>
                <p className="text-gray-700 mb-6">
                  Configure qué tipos de cookies desea permitir. Las cookies esenciales 
                  no se pueden desactivar ya que son necesarias para el funcionamiento del sitio.
                </p>

                <div className="space-y-4 mb-6">
                  {cookieCategories.map((category) => (
                    <div 
                      key={category.key}
                      className="flex items-start justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-medium text-gray-900">
                            {category.name}
                          </h3>
                          {category.required && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              Requeridas
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {category.description}
                        </p>
                      </div>
                      
                      <div className="ml-4">
                        <button
                          onClick={() => handleToggleCategory(category.key)}
                          disabled={category.required}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            tempConsent[category.key]
                              ? 'bg-primary-600'
                              : 'bg-gray-200'
                          } ${category.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          aria-label={`${category.required ? 'Requerido' : 'Alternar'} ${category.name}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              tempConsent[category.key] ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={handleSaveSettings} className="flex-1">
                    <Check className="h-4 w-4 mr-2" />
                    Guardar configuración
                  </Button>
                  <Button 
                    onClick={() => setShowSettings(false)} 
                    variant="outline"
                    className="flex-1"
                  >
                    Volver
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50 rounded-b-lg">
            <p className="text-xs text-gray-500 text-center">
              Puede cambiar sus preferencias en cualquier momento visitando la{' '}
              <Link 
                to="/cookies" 
                className="text-primary-600 hover:text-primary-700 underline"
              >
                página de configuración de cookies
              </Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
